import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { BackButton } from '../components/ui';

const Terms = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8">
          <div className="mb-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Terms of Service</h1>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BackButton
                onClick={() => navigate('/legal')}
                variant="link"
                ariaLabel="Back to Legal Center"
              />
              <span className="text-blue-600 hover:text-blue-500 font-medium">
                Back to Legal Center
              </span>
            </div>
          </div>

          <div className="prose max-w-none">
            <div className="mb-6">
              <p className="text-sm text-gray-500 mb-4">
                Effective Date: [Insert Date]
              </p>
              <p className="text-gray-700 mb-6">
                These Terms of Service ("Terms") govern your access to and use of Vesta's website, mobile apps, and services (collectively, the "Platform"). By creating an account, buying or selling on Vesta, or otherwise using our Platform, you agree to these Terms, our Privacy Policy, and all other applicable Vesta policies.
              </p>
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">1. Our Role</h2>
            <p className="text-gray-700 mb-6">
              Vesta is a platform that enables food creators ("Sellers") to connect with local buyers ("Buyers") for the sale and purchase of food products and related services.
            </p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Vesta is not the seller of food products or services listed on the Platform. Transactions occur directly between Buyers and Sellers.</li>
              <li className="mb-2">We do not prepare, store, deliver, or inspect food — except where explicitly stated in writing.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">2. Eligibility</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">You must be at least 18 years old (or the legal age of majority in your jurisdiction) to create an account.</li>
              <li className="mb-2">By using Vesta, you confirm you are legally able to enter into binding contracts and are not barred from doing so under applicable law.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">3. Account Registration</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">You must create an account to buy or sell on Vesta.</li>
              <li className="mb-2">You are responsible for keeping your account credentials secure.</li>
              <li className="mb-2">You must provide accurate, up-to-date information in your profile, including compliance details if you are a Seller.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">4. Selling on Vesta</h2>
            <p className="text-gray-700 mb-4">Sellers must:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Comply with all applicable food safety laws, including cottage food or commercial kitchen requirements.</li>
              <li className="mb-2">Maintain any required licenses, permits, or certifications.</li>
              <li className="mb-2">Accurately describe products, ingredients, allergens, and preparation methods.</li>
              <li className="mb-2">Follow all Vesta policies, including Food Safety & Compliance, Order, Delivery & Pickup, and Community & Content policies.</li>
            </ul>
            <p className="text-gray-700 mb-6">
              Vesta may request proof of compliance at any time and may remove listings or suspend accounts that fail to meet requirements.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">5. Buying on Vesta</h2>
            <p className="text-gray-700 mb-4">Buyers must:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Read and understand product descriptions, allergens, and handling instructions before purchasing.</li>
              <li className="mb-2">Arrange for timely pickup or accept delivery at the agreed time.</li>
              <li className="mb-2">Follow safe handling practices after receiving food.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">6. Payments & Fees</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Payments are processed through Vesta's designated payment processor.</li>
              <li className="mb-2">Vesta may charge Sellers a transaction fee, service fee, or payment processing fee — disclosed before listing.</li>
              <li className="mb-2">Sellers authorize Vesta to collect amounts owed and deposit net proceeds after fees.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">7. Refunds, Cancellations & Disputes</h2>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Refunds and cancellations are governed by Vesta's Refunds, Cancellations & Disputes Policy.</li>
              <li className="mb-2">Buyers and Sellers should attempt resolution via Vesta's messaging system before escalating to Vesta support.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">8. Prohibited Activities</h2>
            <p className="text-gray-700 mb-4">You may not:</p>
            <ul className="list-disc pl-6 mb-6 text-gray-700">
              <li className="mb-2">Sell food products prohibited by law or Vesta's policies.</li>
              <li className="mb-2">Post misleading, unsafe, or infringing content.</li>
              <li className="mb-2">Harass, threaten, or abuse other members.</li>
              <li className="mb-2">Circumvent Vesta's payment system.</li>
              <li className="mb-2">Use the Platform for illegal purposes.</li>
            </ul>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">9. Content You Post</h2>
            <p className="text-gray-700 mb-6">
              You retain ownership of content you post, but grant Vesta a license to use, display, and distribute it in connection with the Platform. You are responsible for ensuring you have the right to post any content.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">10. Termination</h2>
            <p className="text-gray-700 mb-6">
              We may terminate or suspend your account and bar access to the service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">11. Changes to Terms</h2>
            <p className="text-gray-700 mb-6">
              We reserve the right to modify or replace these terms at any time. If a revision is material, we will provide at least 30 days notice prior to any new terms taking effect.
            </p>

            <h2 className="text-xl font-semibold text-gray-900 mb-4">12. Contact Information</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions about these Terms of Service, please contact us at support@vesta.com
            </p>

            <div className="mt-8 pt-6 border-t border-gray-200">
              <p className="text-sm text-gray-500">
                Last updated: {new Date().toLocaleDateString()}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Terms; 